<template>
  <main-layout wide>
    <base-breadcrumb :routes="routes" />
    <items-grid
      :items="elements"
      :title="folderTitle"
      :can-upload="canUpload"
      :can-delete="canDelete"
      :can-hover="canHover"
      :current-folder="currentFolder"
      :count="count"
      :loading="loading"
      :is-owner="isOwner"
      :public-album="currentFolder.public_album"
      :anyone-can-post="currentFolder.anyone_can_post"
      @search="onSearch"
      @upload-clicked="onUploadClicked"
      @delete="onDeleteItem"
      @reached-bottom="loadMore"
      @selected-image="onSelectedImage"
      @share-clicked="onShareClicked"
      @folder-name-change="onFolderNameChange"
      @public-album="onTogglePublicAlbum"
      @anyone-can-post="onToggleAnyoneCanPost"
    />

    <upload-collateral-modal
      v-model="modal"
      :uploading="uploading"
      :count="nUploaded"
      @upload="onUpload"
    />
    <items-zoom-modal
      v-model="imageIndex"
      :show="imageIndex >= 0"
      :count="count"
      :next-page-exists="nextPageExists"
      :loading="loading"
    />
    <collateral-share-modal
      :show="showShareModal"
      :folder="currentFolder"
      @closed="onShareModalClosed"
      @click="onShareModalClicked"
    />
  </main-layout>
</template>

<script>

import _ from 'lodash';
import api from '@/_api';
import Listable from '@/components/mixins/listable';
import ItemsZoomModal from '@/components/Widgets/Collateral/ItemsZoomModal';
import ItemsGrid from '@/components/Widgets/Collateral/ItemsGrid';
import UploadCollateralModal from '@/components/Widgets/Collateral/UploadCollateralModal';

export default {

  name: 'AlbumLanding',
  components: {
    ItemsGrid,
    ItemsZoomModal,
    UploadCollateralModal,
  },
  mixins: [Listable],
  props: {
    mode: {
      type: String,
      default: 'loadMore',
    },
    source: {
      type: String,
      default: 'collateral',
    },
    pageSize: {
      type: Number,
      default: 40,
    },
  },
  data() {
    return {
      ref: 'widget',
      modal: false,
      imageModal: false,
      uploading: false,
      nUploaded: 0,
      imageIndex: -1,
      showShareModal: false,
    };
  },
  computed: {
    routes() {
      return [
        {
          name: 'Assets',
          link: '/assets',
        },
        {
          name: this.getBreadcrumbName(),
          link: '',
        },
      ];
    },
    folderPk() {
      return this.$route.params.folderId;
    },
    count() {
      return this.$store.state.collateral.count;
    },
    fetchRoute() {
      return this.folderPk === 'shared' ? `${this.source}/getSharedCollages` : Listable.computed.fetchRoute.call(this);
    },
    loading() {
      return this.$store.state.collateral.isLoading;
    },
    isOwner() {
      return this.$store.state.account.user.profile.url === this.currentFolder.owner;
    },
    currentFolder() {
      return this.$store.state.folders.item;
    },
    isManager() {
      return this.$store.state.account.isAdminMode;
    },
    currentUserPk() {
      return this.$store.state.account.user.profile.pk;
    },
    folderTitle() {
      if (this.folderPk !== 'all' && this.folderPk !== 'shared') {
        return this.currentFolder.name;
      }
      return (this.folderPk === 'all' ? 'All photos' : 'Shared Collages');
    },
    /** User can upload if it follows this criteria. */
    canUpload() {
      const canPost = this.isManager || this.isOwner || this.currentFolder.anyone_can_post;
      const isSynced = this.currentFolder.sync_target !== null;
      const isSpecialFolder = this.currentFolder.pk === 'all';
      return this.currentFolder && canPost && !isSynced && !isSpecialFolder;
    },
  },
  beforeMount() {
    // If the folder it's not the 'all' folder, add folder pk as queryparam and bring folder info from back
    /* eslint-disable */
    if (!isNaN(this.folderPk)) {
      
      /** 
       * Important note: you can mutate queryParams at this point but, 
       * it must be like this:
       * this.queryParams.something = "";
       * because this way, the watcher won't be triggered
       * hint: the watcher is not deep
       */

      this.queryParams.folder = this.folderPk;
      this.queryParams.scope =  this.$store.getters['account/scope'];

      this.$store.dispatch('folders/getItem', this.folderPk);
    }
  },
  methods: {
    getBreadcrumbName() {
      if (this.folderPk === 'all') {
        return 'All Photos';
      } if (this.folderPk === 'shared') {
        return 'Shared Collages';
      }
      return this.currentFolder.name;
    },
    onSelectedImage(index) {
      this.imageIndex = index;
    },
    onShareClicked() {
      this.showShareModal = true;
    },
    onShareModalClosed() {
      this.showShareModal = false;
    },
    onShareModalClicked() {
      this.showShareModal = false;
    },
    /* eslint-disable */
    onSearch: _.debounce(function (evt) {
      this.search = evt;
    }, 250),
    onUploadClicked() {
      this.modal = true;
    },
    onTogglePublicAlbum(val) {
      const payload = {
        public_album: val,
        ...(val ? {} : {anyone_can_post: false})
      };
      const folderId = this.folderPk;
      this.$store.dispatch('folders/updateItem', { folderId, payload });
    },
    onToggleAnyoneCanPost(val) {
      const payload = {
        anyone_can_post: val,
        ...(val ? {public_album: true} : {})
      };
      const folderId = this.folderPk;
      this.$store.dispatch('folders/updateItem', { folderId, payload });
    },
    onFolderNameChange(newName) {
      let name = newName.trim();
      const payload = {};
      if (name !== '') {
        payload.name = name;
      } else {
        payload.name = 'New Album';
      }

      const folderId = this.folderPk;
      this.$store.dispatch('folders/updateItem', { folderId, payload });
    },
    onDeleteItem(item) {
      if (this.folderPk === 'shared') {
        const payload = {};
        if (item.uploaderPk === this.currentUserPk) {
          payload.copy = true;
        }
        if (this.isManager) {
          payload.public = false;
        }
        this.$store.dispatch('collateral/hideSharedCollage', { itemPk: item.pk, payload });
        return;
      }
      this.queryParams.id = item.pk;
      this.$store.dispatch('collateral/deleteItemWithoutAll', this.queryParams);
    },
    canDelete(item) {
      if (this.currentFolder.sync_target || this.folderPk === 'shared') {
        return false;
      }
      if (this.isManager) {
        return true;
      }
      if (item.uploader_profile == this.$store.state.account.user.profile.url) {
        return true;
      }
      /* Code that let's you delete shared folders if you own them, leaving this here for future feature.
      const isShared = this.folderPk === 'shared';
      if (isShared) {
        return item.uploaderPk === this.$store.state.account.user.profile.pk;
      }
      */
      return this.isOwner;
    },
    canHover(item) {
      return this.canDelete(item);
    },
    /** 
     * This reset is present in the listable.js mixin.
     * Not sure why was overwritten but the implementation was uncompleted
    */
    // reset() {
    //   this.truePage = 1;
    //   this.fetchData();
    // },
    async onUpload(files) {
      /**
       * Post new collaterals.
       * This is done sequentially, as opposed to in parallel, because when done in parallel the requests aren't
       * resolved in the order they are sent, so the order of the images changes from the one shown in the upload modal.
       * The way it works now does slow down the uploading process, but before optimizing, a better solution for
       * keeping the order needs to be implemented!
      */
      if (this.currentFolder.pk < 0) {
        console.warn('Tried to upload to "ALL"');
        return;
      }
      if (this.uploading) {
        console.warn('Tried to upload while already uploading');
        return;
      }
      let finished = 0;
      this.nUploaded = 0;
      this.uploading = true;
      /* files.reverse() is to preserve order of images to be uploaded as seen in the modal. */
      for (const file of files.reverse()) {
        const formData = new FormData();
        if (file.type.startsWith('image/')){
          formData.append('image', file);
        }else{
          formData.append('file', file);
        }
        formData.append('folder', this.currentFolder.pk >= 0 ? this.currentFolder.url : null);
        await api.post(`/collateral/?scope=${this.$store.getters['account/scope']}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }).then(() => {
          this.nUploaded = this.nUploaded + 1;
        }).catch((error) => {
          console.error('Upload failure: ', error.response && error.response.status ? error.response.status : '?');
        }).finally(() => {
          finished += 1;
          if (finished === files.length) {
            this.uploading = false;
            this.modal = false;
            this.reset();
            this.reload();

            // Trigger notification
            api.post(`/collateral-notification/${this.currentFolder.pk}/`);
          }
        });
      }
    },
  },
};
</script>

<style lang="css" scoped>
.items-grid-container {
  height: 100%;
}
</style>
